<script setup>
import { computed, ref, watch } from 'vue';
import CalendarDateField from './CalendarDateField.vue';
import CalendarTimeField from './CalendarTimeField.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  CALENDAR_TIME_ZONE_LABEL,
  calendarAt,
  calendarDateKey,
  calendarDayEnd,
  calendarDayStart,
  calendarInstant,
  calendarPickerDate,
  calendarTimeValue,
  moveCalendarPeriod,
  pickerDateKey,
  shiftCalendarDays,
} from './calendarTime';

const props = defineProps({
  startsAt: { type: String, default: '' },
  endsAt: { type: String, default: '' },
  allDay: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  minDate: { type: Date, default: null },
});
const emit = defineEmits(['change', 'validityChange']);
const rememberedTimes = ref({ start: '09:00', end: '09:30' });
const extraEndDate = ref(false);
const showEndDate = computed(
  () =>
    extraEndDate.value ||
    Boolean(
      props.startsAt &&
        props.endsAt &&
        calendarDateKey(props.startsAt) !== calendarDateKey(props.endsAt)
    )
);
const startDate = computed(() => calendarPickerDate(props.startsAt));
const endDate = computed(() => calendarPickerDate(props.endsAt));
const startTime = computed(() =>
  props.startsAt ? calendarTimeValue(props.startsAt) : ''
);
const endTime = computed(() =>
  props.endsAt ? calendarTimeValue(props.endsAt) : ''
);
const validTime = value => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
const startInput = ref('');
const endInput = ref('');
watch(
  [startTime, endTime],
  ([start, end]) => {
    startInput.value = start;
    endInput.value = end;
  },
  { immediate: true }
);
const inputValid = computed(
  () =>
    props.allDay || (validTime(startInput.value) && validTime(endInput.value))
);
watch(inputValid, value => emit('validityChange', value), {
  immediate: true,
  flush: 'sync',
});
const normalizeTimeInput = value => {
  const digits = String(value).replace(/\D/g, '').slice(0, 4);
  return digits.length > 2
    ? `${digits.slice(0, 2)}:${digits.slice(2)}`
    : digits;
};

const updateStartDate = value => {
  const date = pickerDateKey(value);
  if (props.disabled || !date || !props.startsAt) return;
  emit(
    'change',
    moveCalendarPeriod(
      {
        starts_at: props.startsAt,
        ends_at: props.endsAt,
        all_day: props.allDay,
      },
      calendarAt(date)
    )
  );
};
const updateStartTime = input => {
  if (props.disabled) return;
  const value = normalizeTimeInput(input);
  startInput.value = value;
  if (!validTime(value) || !props.startsAt) return;
  const nextStart = calendarAt(calendarDateKey(props.startsAt), value);
  const duration = props.endsAt
    ? calendarInstant(props.endsAt) - calendarInstant(props.startsAt)
    : 1800000;
  emit('change', {
    starts_at: nextStart.toISOString(),
    ends_at: new Date(
      nextStart.getTime() + Math.max(duration, 60000)
    ).toISOString(),
  });
};
const updateEndDate = value => {
  const date = pickerDateKey(value);
  if (props.disabled || !date) return;
  emit('change', {
    ends_at: (props.allDay
      ? calendarDayEnd(calendarAt(date))
      : calendarAt(date, endTime.value || '09:30')
    ).toISOString(),
  });
};
const updateEndTime = input => {
  if (props.disabled) return;
  const value = normalizeTimeInput(input);
  endInput.value = value;
  if (!validTime(value) || !props.startsAt) return;
  let end = calendarAt(calendarDateKey(props.endsAt || props.startsAt), value);
  // A time before the start on the same date is an overnight event.
  if (
    end <= calendarInstant(props.startsAt) &&
    calendarDateKey(end) === calendarDateKey(props.startsAt)
  ) {
    end = calendarAt(calendarDateKey(shiftCalendarDays(end, 1)), value);
  }
  emit('change', { ends_at: end.toISOString() });
};
const toggleAllDay = value => {
  if (props.disabled || !props.startsAt) return;
  if (value) {
    rememberedTimes.value = {
      start: startTime.value,
      end: endTime.value || '09:30',
    };
    emit('change', {
      all_day: true,
      starts_at: calendarDayStart(props.startsAt).toISOString(),
      ends_at: calendarDayEnd(props.endsAt || props.startsAt).toISOString(),
    });
    return;
  }
  const start = calendarAt(
    calendarDateKey(props.startsAt),
    rememberedTimes.value.start
  );
  let end = calendarAt(
    calendarDateKey(props.endsAt || props.startsAt),
    rememberedTimes.value.end
  );
  if (end <= start) end = new Date(start.getTime() + 1800000);
  emit('change', {
    all_day: false,
    starts_at: start.toISOString(),
    ends_at: end.toISOString(),
  });
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <fieldset
    :disabled="disabled"
    class="event-period m-0 grid min-w-0 gap-3 border-0 p-0"
  >
    <legend class="sr-only">Data e horário do evento</legend>
    <div class="flex min-w-0 flex-wrap items-end gap-3">
      <div class="grid min-w-0 flex-[1_1_17rem] gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">Data</span>
        <CalendarDateField
          :value="startDate"
          :min-date="minDate"
          :disabled="disabled"
          label="Data de início"
          data-testid="event-start-date"
          @change="updateStartDate"
        />
      </div>
      <div
        v-if="!allDay"
        class="flex min-w-0 flex-[1_1_22rem] flex-wrap items-end gap-2"
      >
        <CalendarTimeField
          :model-value="startInput"
          label="Início"
          :disabled="disabled"
          class="min-w-[9rem] flex-1"
          data-testid="event-start-time"
          @update:model-value="updateStartTime"
        />
        <span class="mb-2 text-n-slate-10" aria-hidden="true">–</span>
        <CalendarTimeField
          :model-value="endInput"
          label="Fim"
          :disabled="disabled"
          class="min-w-[9rem] flex-1"
          data-testid="event-end-time"
          @update:model-value="updateEndTime"
        />
      </div>
    </div>
    <div v-if="showEndDate" class="grid min-w-0 gap-1.5">
      <span class="text-sm font-medium text-n-slate-12">Data de término</span>
      <CalendarDateField
        :value="endDate"
        :min-date="
          startDate && (!minDate || startDate > minDate) ? startDate : minDate
        "
        :disabled="disabled"
        label="Data de término"
        class="sm:max-w-sm"
        data-testid="event-end-date"
        @change="updateEndDate"
      />
    </div>
    <button
      v-else
      type="button"
      class="w-fit text-xs font-medium text-n-brand hover:underline disabled:opacity-50"
      :disabled="disabled"
      data-testid="event-other-end-date"
      @click="extraEndDate = true"
    >
      Outra data de término
    </button>
    <p v-if="!inputValid" class="m-0 text-xs text-n-ruby-11" role="alert">
      Use horários válidos no formato 24 horas, como 09:30.
    </p>
    <div class="flex flex-wrap items-center justify-between gap-3">
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <Switch
          :model-value="allDay"
          :disabled="disabled"
          aria-label="Dia inteiro"
          data-testid="event-all-day"
          @update:model-value="toggleAllDay"
        />
        Dia inteiro
      </label>
      <span
        class="flex items-center gap-1.5 text-xs text-n-slate-11"
        data-testid="event-timezone"
      >
        <span class="i-lucide-globe size-3.5" aria-hidden="true" />
        {{ CALENDAR_TIME_ZONE_LABEL }}
      </span>
    </div>
  </fieldset>
</template>
